import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, FileText, Settings, LogOut, Menu, X,
  ChevronRight, TrendingUp, Clock, AlertCircle, CheckCircle, Loader2,
  Shield, Zap, BarChart3, Globe, Bell, Database, CreditCard
} from 'lucide-react';
import AdminDashboard from '@/components/admin/AdminDashboard';
import AdminCasesView from '@/components/admin/AdminCases';
import AdminUsersView from '@/components/admin/AdminUsers';
import AdminAuditLogs from '@/components/admin/AdminAuditLogs';
import AdminAssignments from '@/components/admin/AdminAssignments';
import AdminPayments from '@/components/admin/AdminPayments';
import AdminWhatsAppInbox from '@/components/admin/AdminWhatsAppInbox';
import AdminAnalytics from '@/components/admin/AdminAnalytics';
import AdminStaff from '@/components/admin/AdminStaff';
import AdminAutomation from '@/components/admin/AdminAutomation';
import AdminPlaywright from '@/components/admin/AdminPlaywright';
import AdminTemplates from '@/components/admin/AdminTemplates';
import AdminDocuments from '@/components/admin/AdminDocuments';
import AdminPayloadBuilder from '@/components/admin/AdminPayloadBuilder';
import AdminComplianceLogs from '@/components/admin/AdminComplianceLogs';

function SidebarItem({ icon: Icon, label, to, active }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition ${
        active ? 'bg-[#00a884] text-white' : 'text-[#aebac1] hover:bg-[#2a3942] hover:text-white'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{label}</span>
      {active && <ChevronRight className="w-4 h-4 ml-auto" />}
    </Link>
  );
}

function StatCard({ title, value, change, icon: Icon, color }) {
  return (
    <div className="bg-[#111b21] rounded-xl p-5 border border-[#2a3942]">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {change && (
          <span className={`text-xs font-medium ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {change >= 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <p className="text-[#8696a0] text-sm">{title}</p>
      <p className="text-white text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function AdminSettings() {
  const [settings, setSettings] = useState({
    ai_model: 'openai/gpt-4o',
    ai_fallback: 'anthropic/claude-sonnet-4.5',
    default_language: 'en',
    enable_otp: true,
    enable_payments: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/settings', settings);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Settings</h2>
        <p className="text-[#8696a0] text-sm mt-1">Configure platform settings</p>
      </div>

      <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-6 space-y-6">
        <div>
          <label className="text-white text-sm font-medium block mb-2">Primary AI Model</label>
          <select 
            value={settings.ai_model} 
            onChange={(e) => setSettings({...settings, ai_model: e.target.value})}
            className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm"
          >
            <option value="openai/gpt-4o">OpenAI GPT-4o</option>
            <option value="openai/gpt-4o-mini">OpenAI GPT-4o Mini</option>
            <option value="anthropic/claude-sonnet-4.5">Anthropic Claude Sonnet 4.5</option>
            <option value="anthropic/claude-opus-4.7">Anthropic Claude Opus 4.7</option>
          </select>
        </div>

        <div>
          <label className="text-white text-sm font-medium block mb-2">Fallback AI Model</label>
          <select 
            value={settings.ai_fallback} 
            onChange={(e) => setSettings({...settings, ai_fallback: e.target.value})}
            className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm"
          >
            <option value="anthropic/claude-sonnet-4.5">Anthropic Claude Sonnet 4.5</option>
            <option value="openai/gpt-4o">OpenAI GPT-4o</option>
            <option value="google/gemini-2.5-pro">Google Gemini 2.5 Pro</option>
          </select>
        </div>

        <div>
          <label className="text-white text-sm font-medium block mb-2">Default Language</label>
          <select 
            value={settings.default_language} 
            onChange={(e) => setSettings({...settings, default_language: e.target.value})}
            className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm"
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="mr">Marathi</option>
            <option value="ta">Tamil</option>
            <option value="te">Telugu</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-medium">Enable OTP Verification</p>
            <p className="text-[#8696a0] text-xs">Require mobile OTP for client login</p>
          </div>
          <button 
            onClick={() => setSettings({...settings, enable_otp: !settings.enable_otp})}
            className={`w-12 h-6 rounded-full transition ${settings.enable_otp ? 'bg-[#00a884]' : 'bg-[#374045]'}`}
          >
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.enable_otp ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-medium">Enable Payments</p>
            <p className="text-[#8696a0] text-xs">Razorpay integration for paid services</p>
          </div>
          <button 
            onClick={() => setSettings({...settings, enable_payments: !settings.enable_payments})}
            className={`w-12 h-6 rounded-full transition ${settings.enable_payments ? 'bg-[#00a884]' : 'bg-[#374045]'}`}
          >
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.enable_payments ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <button 
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-[#00a884] text-white rounded-lg text-sm font-medium hover:bg-[#06cf9c] transition disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}


function AdminModule({ title, description }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        <p className="text-[#aebac1] text-sm mt-1">{description}</p>
      </div>
      <div className="admin-card p-5">
        <p className="text-sm text-[#d1d7db]">Admin-controlled workflow controls are available through the backend command-center APIs. Use this module to review queues, approvals, statuses, and audit trails.</p>
      </div>
    </div>
  );
}

function AIGatewayUsage() {
  const [usage, setUsage] = useState(null);
  useEffect(() => { api.get('/admin/ai-gateway/usage').then(({data}) => setUsage(data)).catch(() => toast.error('Failed to load AI Gateway usage')); }, []);
  const ops = usage?.operations || [];
  return (
    <div className="space-y-5">
      <div><h2 className="text-2xl font-bold text-white">AI Gateway Usage</h2><p className="text-[#aebac1] text-sm">All AI calls are routed through the backend Vercel AI Gateway service.</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[['AI calls today', usage?.calls_today || 0], ['Failed calls', usage?.failed_calls || 0], ['Fallback count', usage?.fallback_count || 0], ['Slow calls', usage?.slow_calls || 0]].map(([k,v]) => <div key={k} className="admin-card p-4"><p className="text-[#aebac1] text-xs">{k}</p><p className="text-2xl font-bold">{v}</p></div>)}
      </div>
      <div className="admin-card overflow-x-auto">
        <table className="admin-table text-sm"><thead><tr><th className="text-left p-3">Operation</th><th className="text-left p-3">Model</th><th className="text-left p-3">Latency</th><th className="text-left p-3">Fallback</th><th className="text-left p-3">Last Error</th><th className="text-left p-3">Cost</th></tr></thead><tbody>
          {ops.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-[#aebac1]">No AI usage recorded in this process yet.</td></tr> : ops.map((o,i)=><tr key={i} className="border-t border-[#2a3942]"><td className="p-3">{o.operation_type}</td><td className="p-3">{o.model}</td><td className="p-3">{o.latency_ms}ms</td><td className="p-3">{o.fallback_used ? 'Yes' : 'No'}</td><td className="p-3 text-red-300">{o.error_message || '—'}</td><td className="p-3">{o.estimated_cost || '—'}</td></tr>)}
        </tbody></table>
      </div>
    </div>
  );
}
export default function AdminPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role?.toLowerCase() !== 'admin') {
      navigate('/login/staff');
    }
  }, [user, navigate]);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0b141a] text-white">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#111b21] border-b border-[#2a3942] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#00a884] rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold">Admin</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'fixed inset-0 z-50 bg-[#111b21]' : 'hidden'} lg:block lg:static lg:w-64 lg:h-screen lg:border-r lg:border-[#2a3942] flex-shrink-0`}>
          <div className="p-4 lg:p-6">
            <div className="hidden lg:flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[#00a884] rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Admin Portal</h1>
                <p className="text-xs text-[#8696a0]">{user?.email}</p>
              </div>
            </div>

            <nav className="space-y-1 max-h-[70vh] overflow-y-auto">
              {[
                ['Dashboard','/admin',LayoutDashboard], ['Cases','/admin/cases',FileText], ['Case Probability','/admin/case-probability',BarChart3],
                ['Staff Onboarding','/admin/staff-onboarding',Users], ['Lawyer Approval','/admin/lawyer-approval',Users], ['Agent Approval','/admin/agent-approval',Users],
                ['Assignments','/admin/assignments',Users], ['Reassignments','/admin/reassignments',Users],
                ['Automation Profiles','/admin/automation-profiles',Database], ['Payload Builder','/admin/payload-builder',Database], ['Playwright Runs','/admin/playwright-runs',Globe],
                ['Templates','/admin/templates',FileText], ['Template Visibility','/admin/template-visibility',FileText], ['Payments','/admin/payments',CreditCard],
                ['Invoices','/admin/invoices',CreditCard], ['Refunds','/admin/refunds',CreditCard], ['Final Documents','/admin/final-documents',FileText],
                ['Customer Links','/admin/customer-links',Globe], ['WhatsApp Inbox','/admin/whatsapp',Bell], ['AI Gateway Usage','/admin/ai-gateway',Zap],
                ['Users & Roles','/admin/users',Users], ['Audit Logs','/admin/audit-logs',Shield], ['Compliance Log','/admin/compliance',Shield], ['Settings','/admin/settings',Settings],
              ].map(([label,to,Icon]) => <SidebarItem key={to} icon={Icon} label={label} to={to} active={isActive(to)} />)}
</nav>

            <div className="mt-8 pt-6 border-t border-[#2a3942]">
              <button 
                onClick={logout}
                className="flex items-center gap-3 px-4 py-3 text-[#aebac1] hover:text-red-400 transition w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/cases" element={<AdminCasesView />} />
            <Route path="/case-probability" element={<AdminAnalytics />} />
            <Route path="/staff-onboarding" element={<AdminStaff />} />
            <Route path="/lawyer-approval" element={<AdminStaff roleFilter="lawyer" />} />
            <Route path="/agent-approval" element={<AdminStaff roleFilter="agent" />} />
            <Route path="/assignments" element={<AdminAssignments />} />
            <Route path="/reassignments" element={<AdminModule title="Reassignments" description="Reassignment history with old user, new user, reason, actor, and timestamp." />} />
            <Route path="/automation-profiles" element={<AdminAutomation />} />
            <Route path="/payload-builder" element={<AdminPayloadBuilder />} />
            <Route path="/playwright-runs" element={<AdminPlaywright />} />
            <Route path="/templates" element={<AdminTemplates />} />
            <Route path="/template-visibility" element={<AdminTemplates />} />
            <Route path="/payments" element={<AdminPayments />} />
            <Route path="/invoices" element={<AdminModule title="Invoices" description="Generate, resend, view, and download invoices." />} />
            <Route path="/refunds" element={<AdminModule title="Refunds" description="Approve or reject refunds with required admin reason and audit trail." />} />
            <Route path="/final-documents" element={<AdminDocuments />} />
            <Route path="/customer-links" element={<AdminDocuments />} />
            <Route path="/whatsapp" element={<AdminWhatsAppInbox />} />
            <Route path="/ai-gateway" element={<AIGatewayUsage />} />
            <Route path="/users" element={<AdminUsersView />} />
            <Route path="/audit-logs" element={<AdminAuditLogs />} />
            <Route path="/compliance" element={<AdminComplianceLogs />} />
            <Route path="/settings" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
